import { getAuth } from 'firebase/auth';
import { safeHttpsCallable } from './firebase';
import type { 
  SearchResultsResponse, 
  MapLocationsResponse, 
  SightingResponse,
  AlgoliaUsageResponse,
  SearchFilters,
  AggregationStatus,
  AlgoliaStatus
} from '../types/firestore';

// Define Base URL based on environment
const API_BASE_URL = import.meta.env.DEV
  ? 'http://127.0.0.1:5001/soda-tracker-455105/us-central1/api'
  : 'https://us-central1-soda-tracker-455105.cloudfunctions.net/api'; // Production URL for Firebase Functions

// Helper to get authentication token
const getAuthToken = async (): Promise<string> => {
  const auth = getAuth();
  const user = auth.currentUser;
  
  if (!user) {
    throw new Error('User not authenticated');
  }
  
  return await user.getIdToken();
};

// Helper to check if user has the required permission
// We'll rely on the backend to enforce permissions, client-side is just for UX
const checkUserPermission = async (): Promise<void> => {
  const auth = getAuth();
  const user = auth.currentUser;
  
  if (!user) {
    throw new Error('User not authenticated');
  }
  
  // This is a simple check that can be bypassed client-side,
  // but the real enforcement will happen on the server with the token
  try {
    // We'll use the token to check permissions on the server side
    await getAuthToken();
  } catch (error) {
    throw new Error('Authentication failed');
  }
};

// Map bounds interface
interface MapBounds {
  ne_lat: number;
  ne_lng: number;
  sw_lat: number;
  sw_lng: number;
}

// Enhanced Map Filters interface
interface MapFilters {
  locationType?: string;
  hasDrink?: string | string[];
  hasBrand?: string | string[];
  hasFlavor?: string | string[];
  hasAttribute?: string | string[];
  brandSize?: 'craft' | 'mass' | 'unknown' | 'all';
  minRating?: number;
  startDate?: string | null;
  endDate?: string | null;
  page?: number;
  hitsPerPage?: number;
}

/**
 * Convert filter object with potential array values to URLSearchParams
 * Handles arrays by repeating the key.
 * @param filters - Object with filter values based on SearchFilters interface
 * @returns Record ready to be converted to URLSearchParams
 */
function prepareFilters(filters: Partial<SearchFilters>): Record<string, string> {
  const params: Record<string, string> = {};
  
  Object.entries(filters).forEach(([key, value]) => {
    // Skip undefined or null values entirely
    if (value === undefined || value === null) {
      return;
    }
    
    if (key === 'query' && typeof value === 'string' && value.trim() === '') {
      // Skip empty query strings
      return;
    }

    // Handle dates - convert to ISO string
    if (value instanceof Date) {
      params[key] = value.toISOString();
      return;
    }
    
    // Handle boolean 'useMapBounds' - convert to string
    if (key === 'useMapBounds' && typeof value === 'boolean') {
      params[key] = value.toString();
      return;
    }

    // Handle arrays by repeating the parameter key
    if (Array.isArray(value)) {
      // Only add if the array is not empty
      if (value.length > 0) {
        // We will add these later to handle URLSearchParams limitations
      }
    } else {
      // Handle numbers, strings, etc.
      params[key] = value.toString();
    }
  });

  // Now handle arrays separately to correctly add repeated keys
  Object.entries(filters).forEach(([key, value]) => {
    if (Array.isArray(value) && value.length > 0) {
      // For URLSearchParams, we need to handle array params manually
      // by appending them. This logic will be applied where URLSearchParams is created.
      // For now, just ensure the key exists if the array is not empty
      // This placeholder indicates the key should be handled specially later.
      params[`__array_${key}`] = 'true'; 
    }
  });
  
  return params;
}

/**
 * Helper function to build URLSearchParams, handling array parameters correctly.
 */
function buildSearchParams(baseParams: Record<string, string>, filters: Partial<SearchFilters>): URLSearchParams {
  const searchParams = new URLSearchParams(baseParams);

  // Append array values correctly
  Object.entries(filters).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach(item => {
        if (item !== null && item !== undefined) {
          searchParams.append(key, item.toString());
        }
      });
    } else if (key !== 'useMapBounds' && key !== 'query' && value !== null && value !== undefined) {
       // Add non-array, non-special handled values if they weren't added by prepareFilters initially
       // This covers cases missed if prepareFilters logic changes
       if (!baseParams.hasOwnProperty(key)) {
          searchParams.set(key, value.toString());
       }
    }
  });
  
   // Ensure boolean 'useMapBounds' is correctly added if present
  if (filters.useMapBounds !== undefined && filters.useMapBounds !== null) {
    searchParams.set('useMapBounds', filters.useMapBounds.toString());
  }
  
  // Ensure query is correctly added if present and not empty
  if (filters.query && filters.query.trim() !== '') {
    searchParams.set('query', filters.query);
  }


  return searchParams;
}

/**
 * Search for drinks with comprehensive filtering options
 * @param query - Search query string (can also be passed in filters object)
 * @param filters - Object with filter values based on SearchFilters
 * @returns Search results response
 */
export async function searchDrinks(
  query: string = '', 
  filters: Partial<SearchFilters> = {}
): Promise<SearchResultsResponse> {
  try {
    // Try to get authentication but don't fail immediately if not authenticated
    let token = '';
    try {
      await checkUserPermission();
      token = await getAuthToken();
      console.log('[API DEBUG] Got auth token successfully');
    } catch (authError) {
      console.warn('[API DEBUG] Authentication failed, proceeding without token:', authError);
      // Continue without a token - the API might still work for public data
    }
    
    // Combine query param with filters object, giving precedence to filters.query
    const effectiveQuery = filters.query ?? query;
    const finalFilters = { ...filters, query: effectiveQuery };
    
    const prepared = prepareFilters(finalFilters);
    const params = buildSearchParams({ type: 'drinks', ...prepared }, finalFilters);
    
    const fullUrl = `${API_BASE_URL}/search?${params.toString()}`;
    
    // Add diagnostic logging
    console.log('[API DEBUG] Sending request to:', fullUrl);
    console.log('[API DEBUG] Using token:', token ? `${token.substring(0, 10)}...` : 'missing');
    
    // Prepare headers with or without token
    const headers: Record<string, string> = {
      'Accept': 'application/json'
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const response = await fetch(fullUrl, {
      headers,
      mode: 'cors',
      credentials: 'same-origin'
    });
    
    console.log('[API DEBUG] Response status:', response.status, response.statusText);
    console.log('[API DEBUG] Response headers:', [...response.headers.entries()]);
    
    if (!response.ok) {
      // Try to get text to see what's coming back
      const responseText = await response.text();
      console.error('[API DEBUG] Error response body:', responseText.substring(0, 500));
      const errorData = { error: `HTTP error: ${response.status}` };
      throw new Error(errorData.error || 'Search failed');
    }
    
    // Check the content type
    const contentType = response.headers.get('content-type');
    console.log('[API DEBUG] Response content-type:', contentType);
    
    // If not JSON content type, handle differently
    if (!contentType || !contentType.includes('application/json')) {
      const responseText = await response.text();
      console.error('[API DEBUG] Non-JSON response:', responseText.substring(0, 500));
      throw new Error('Response was not JSON format');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error searching drinks:', error);
    throw error;
  }
}

/**
 * Search for locations with comprehensive filtering options
 * @param query - Search query string (can also be passed in filters object)
 * @param filters - Object with filter values based on SearchFilters
 * @returns Search results response
 */
export async function searchLocations(
  query: string = '', 
  filters: Partial<SearchFilters> = {}
): Promise<SearchResultsResponse> {
  try {
    // Try to get authentication but don't fail immediately if not authenticated
    let token = '';
    try {
      await checkUserPermission();
      token = await getAuthToken();
      console.log('[API DEBUG] Got auth token successfully');
    } catch (authError) {
      console.warn('[API DEBUG] Authentication failed, proceeding without token:', authError);
      // Continue without a token - the API might still work for public data
    }
    
    const effectiveQuery = filters.query ?? query;
    const finalFilters = { ...filters, query: effectiveQuery };

    const prepared = prepareFilters(finalFilters);
    const params = buildSearchParams({ type: 'locations', ...prepared }, finalFilters);

    const fullUrl = `${API_BASE_URL}/search?${params.toString()}`;
    
    // Add diagnostic logging
    console.log('[API DEBUG] Sending locations request to:', fullUrl);
    console.log('[API DEBUG] Using token:', token ? `${token.substring(0, 10)}...` : 'missing');
    
    // Prepare headers with or without token
    const headers: Record<string, string> = {
      'Accept': 'application/json'
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const response = await fetch(fullUrl, {
      headers,
      mode: 'cors',
      credentials: 'same-origin'
    });
    
    console.log('[API DEBUG] Locations response status:', response.status, response.statusText);
    
    if (!response.ok) {
      // Try to get text to see what's coming back
      const responseText = await response.text();
      console.error('[API DEBUG] Error response body:', responseText.substring(0, 500));
      const errorData = { error: `HTTP error: ${response.status}` };
      throw new Error(errorData.error || 'Search failed');
    }
    
    // Check the content type
    const contentType = response.headers.get('content-type');
    console.log('[API DEBUG] Response content-type:', contentType);
    
    // If not JSON content type, handle differently
    if (!contentType || !contentType.includes('application/json')) {
      const responseText = await response.text();
      console.error('[API DEBUG] Non-JSON response:', responseText.substring(0, 500));
      throw new Error('Response was not JSON format');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error searching locations:', error);
    throw error;
  }
}

/**
 * Search across all entity types with comprehensive filtering
 * @param query - Search query string (can also be passed in filters object)
 * @param filters - Object with filter values based on SearchFilters
 * @returns Search results response
 */
export async function searchAll(
  query: string = '', 
  filters: Partial<SearchFilters> = {}
): Promise<SearchResultsResponse> {
  try {
    // Try to get authentication but don't fail immediately if not authenticated
    let token = '';
    try {
      await checkUserPermission();
      token = await getAuthToken();
      console.log('[API DEBUG] Got auth token successfully');
    } catch (authError) {
      console.warn('[API DEBUG] Authentication failed, proceeding without token:', authError);
      // Continue without a token - the API might still work for public data
    }
    
    const effectiveQuery = filters.query ?? query;
    const finalFilters = { ...filters, query: effectiveQuery };

    const prepared = prepareFilters(finalFilters);
    const params = buildSearchParams({ type: 'all', ...prepared }, finalFilters);

    const fullUrl = `${API_BASE_URL}/search?${params.toString()}`;
    
    // Add diagnostic logging
    console.log('[API DEBUG] Sending all search request to:', fullUrl);
    console.log('[API DEBUG] Using token:', token ? `${token.substring(0, 10)}...` : 'missing');
    
    // Prepare headers with or without token
    const headers: Record<string, string> = {
      'Accept': 'application/json'
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    
    const response = await fetch(fullUrl, {
      headers,
      mode: 'cors',
      credentials: 'same-origin'
    });
    
    console.log('[API DEBUG] All search response status:', response.status, response.statusText);
    
    if (!response.ok) {
      // Try to get text to see what's coming back
      const responseText = await response.text();
      console.error('[API DEBUG] Error response body:', responseText.substring(0, 500));
      const errorData = { error: `HTTP error: ${response.status}` };
      throw new Error(errorData.error || 'Search failed');
    }
    
    // Check the content type
    const contentType = response.headers.get('content-type');
    console.log('[API DEBUG] Response content-type:', contentType);
    
    // If not JSON content type, handle differently
    if (!contentType || !contentType.includes('application/json')) {
      const responseText = await response.text();
      console.error('[API DEBUG] Non-JSON response:', responseText.substring(0, 500));
      throw new Error('Response was not JSON format');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error searching all:', error);
    throw error;
  }
}

/**
 * Get locations for a map view with filtering
 * @param bounds - Map viewport bounds
 * @param filters - Object with filter values
 * @returns Map locations response
 */
export async function getMapLocations(
  bounds: MapBounds, 
  filters: MapFilters = {}
): Promise<MapLocationsResponse> {
  try {
    await checkUserPermission();
    
    const boundsParams = Object.entries(bounds).reduce((acc, [key, value]) => {
      acc[key] = value.toString();
      return acc;
    }, {} as Record<string, string>);

    const params = new URLSearchParams({
      ...boundsParams,
      ...prepareFilters(filters)
    });
    
    const token = await getAuthToken();
    const response = await fetch(`${API_BASE_URL}/map/locations?${params}`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to fetch map locations');
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error getting map locations:', error);
    throw error;
  }
}

// Interface for the payload of a single sighting
export interface SightingPayload {
  locationId: string;
  drinkId: string;
}

/**
 * Report a single sighting or multiple sightings in a batch.
 * @param payload - A single SightingPayload object or an array of SightingPayload objects.
 * @returns Sighting response (the backend might return a summary or array of results for batch).
 */
export async function reportSighting(
  payload: SightingPayload | SightingPayload[]
): Promise<SightingResponse> { // SightingResponse might need adjustment for batch cases if backend is different
  try {
    await checkUserPermission();
    const token = await getAuthToken();

    const isBatch = Array.isArray(payload);
    const operationDescription = isBatch ? `batch sighting report for ${payload.length} items` : "single sighting report";
    console.log(`Starting ${operationDescription} with token ${token.substring(0, 10)}...`);

    const url = `${API_BASE_URL}/sightings?nocache=${Date.now()}`;
    console.log(`Reporting to ${url} with token`, token ? 'present' : 'missing');

    const body = JSON.stringify(payload);
    console.log(`Request body: ${body}`);

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body
    });

    console.log(`${isBatch ? 'Batch' : 'Single'} sighting response status:`, response.status);

    // Handle potential error responses (e.g., 412 for already confirmed)
    // The backend summary mentions "Returns appropriate status codes based on whether it's a single or batch request."
    if (response.status === 412) {
      const errorData = await response.json().catch(() => ({})); // Safely parse JSON
      let errorMessage = 'Failed to report sighting(s) due to a conflict.';
      if (errorData.errorCode === 'ALREADY_CONFIRMED_TODAY') {
        errorMessage = isBatch 
          ? 'One or more sightings in the batch have already been confirmed today.'
          : 'You have already confirmed this sighting today';
      }
      throw new Error(errorData.error || errorMessage);
    }

    let responseText;
    try {
      responseText = await response.text();
      console.log(`Raw ${isBatch ? 'batch' : 'single'} response:`, responseText);

      if (!response.ok) {
        let errorMessage = `HTTP error: ${response.status}`;
        try {
          const errorJson = JSON.parse(responseText);
          if (errorJson && errorJson.error) {
            errorMessage += `, ${errorJson.error}`;
          } else if (errorJson && errorJson.message) {
            errorMessage += `, ${errorJson.message}`;
          } else {
            errorMessage += `, ${responseText}`;
          }
        } catch (parseError) {
          errorMessage += `, ${responseText}`;
        }
        throw new Error(errorMessage);
      }
      
      // The backend returns SightingResponse for single and potentially a modified structure for batch.
      // For now, we cast to SightingResponse. This might need adjustment if batch responses differ significantly.
      return JSON.parse(responseText) as SightingResponse;
    } catch (error) {
      console.error(`${isBatch ? 'Batch' : 'Single'} sighting processing error:`, error);
      throw new Error(`Failed to report ${isBatch ? 'batch' : 'single'} sighting(s): ${responseText || 'No response text'}`);
    }
  } catch (error) {
    // Move isBatch check into the catch block since it's not in scope here
    // We can determine batch status from the payload again
    const wasBatch = Array.isArray(payload);
    console.error(`${wasBatch ? 'Batch' : 'Single'} sighting submission error:`, error);
    if ((error as Error).message === 'User not authenticated') {
      throw new Error('You must be logged in to report sightings');
    }
    throw error;
  }
}

/**
 * Test API connectivity
 * @returns Test response
 */
export async function testApi(): Promise<any> {
  try {
    // Use absolute URL for emulator or relative for production
    const url = `${API_BASE_URL}/test?nocache=${Date.now()}`;
    console.log(`Testing API endpoint: ${url}`);

    // Add explicit CORS mode to diagnose issues
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
      },
      mode: 'cors' // Explicitly set CORS mode
    });

    console.log('Test API response status:', response.status);

    let responseData;
    try {
      responseData = await response.json();
      console.log('Raw response:', responseData);

      if (!response.ok) {
        throw new Error(`HTTP error: ${response.status}, ${responseData.error || 'Unknown error'}`);
      }

      return responseData;
    } catch (error) {
      console.error('Test API error parsing response:', error);
      // Try to get text if JSON fails
      const textResponse = await response.text().catch(() => 'Could not read response text');
      throw new Error(`Failed to test API: ${textResponse}`);
    }
  } catch (error) {
    console.error('Test API error:', error);
    
    // Check if this is a CORS error
    if (error instanceof TypeError && error.message.includes('Failed to fetch')) {
      console.error('This appears to be a CORS error. Make sure your Firebase emulator has CORS enabled.');
      console.error('Try using a CORS browser extension temporarily, or update your Firebase functions to support CORS.');
    }
    
    throw error; // Re-throw the error to be caught by the caller
  }
}

// ==== ALGOLIA ADMIN FUNCTIONS ====

// ==== DEPRECATED FUNCTIONS REMOVED ====

/**
 * Clear all Algolia indices
 * @returns API response
 */
export async function clearAlgoliaIndices(): Promise<any> {
  try {
    await checkUserPermission();
    const result = await safeHttpsCallable('clearAlgoliaIndices');
    return result.data;
  } catch (error) {
    console.error('Error clearing Algolia indices:', error);
    throw error;
  }
}

/**
 * Get Algolia usage statistics
 * @returns API response with usage data including separate counts for drinks and locations
 */
export async function getAlgoliaUsage(): Promise<AlgoliaUsageResponse> {
  try {
    await checkUserPermission();
    const result = await safeHttpsCallable('getAlgoliaUsage');
    return result.data as AlgoliaUsageResponse;
  } catch (error) {
    console.error('Error getting Algolia usage:', error);
    throw error;
  }
}

// ==== NEW SYSTEM STATUS FUNCTIONS ====

/**
 * Get the current system status for both Aggregation and Algolia Sync.
 * @returns Object containing the status for aggregation and algolia.
 */
export async function getSystemStatus(): Promise<{ aggregation: AggregationStatus; algolia: AlgoliaStatus }> {
  try {
    await checkUserPermission();
    
    const result = await safeHttpsCallable('getSystemStatus');
    return result.data as { aggregation: AggregationStatus; algolia: AlgoliaStatus };
  } catch (error) {
    console.error('Error getting system status:', error);
    throw error;
  }
}

// Define the expected response structure for the setSystemStatus cloud function
interface SetSystemStatusResponse {
  success: boolean;
  updatedTargets: string[];
  message?: string; // Optional message from backend
}

export async function setSystemStatus(statusUpdates: { 
  aggregationStatus?: 'enabled' | 'disabled'; 
  algoliaStatus?: 'enabled' | 'disabled'; 
}): Promise<SetSystemStatusResponse> { // Changed return type
  try {
    // Send statusUpdates directly - safeHttpsCallable will wrap it in { data: ... } automatically
    const result = await safeHttpsCallable<typeof statusUpdates, SetSystemStatusResponse>(
      'setSystemStatus',
      statusUpdates
    );
    return result.data; // safeHttpsCallable returns { data: R }
  } catch (error) {
    console.error('Error calling setSystemStatus callable:', error);
    // Construct a fallback error response that matches SetSystemStatusResponse
    const errorMessage = error instanceof Error ? error.message : 'Unknown error setting system status';
    return {
      success: false,
      updatedTargets: [],
      message: errorMessage
    };
  }
}

/**
 * Trigger a full recalculation of all aggregated fields.
 * Note: This disables system statuses; they must be manually re-enabled via setSystemStatus.
 * @returns API response indicating success and results.
 */
export async function performFullAggregation(): Promise<{ success: boolean; results: any; message: string }> {
  try {
    await checkUserPermission();
    const result = await safeHttpsCallable('performFullAggregation');
    return result.data as { success: boolean; results: any; message: string };
  } catch (error) {
    console.error('Error performing full aggregation:', error);
    throw error;
  }
}

/**
 * Trigger a full synchronization of Firestore data to Algolia.
 * @param options - Optional parameters (e.g., { indices: ['drinks'] } to sync only specific indices).
 * @returns API response indicating success and sync statistics.
 */
export async function performFullAlgoliaSync(options?: { indices?: ('drinks'|'locations')[] }): Promise<{ success: boolean; stats: { drinks: number; locations: number } }> {
  try {
    await checkUserPermission();
    const result = await safeHttpsCallable('performFullAlgoliaSync', options);
    return result.data as { success: boolean; stats: { drinks: number; locations: number } };
  } catch (error) {
    console.error('Error performing full Algolia sync:', error);
    throw error;
  }
}

/**
 * Export the current Firestore data using the Emulator REST API
 * @returns Object containing the exported data and a timestamp
 */
export async function exportFirestoreDataViaEmulator(): Promise<any> {
  try {
    await checkUserPermission();

    const isEmulator = import.meta.env.DEV;
    if (!isEmulator) {
      throw new Error('Data export via Emulator REST API is only available in development mode');
    }

    // Firestore emulator typically runs on port 8080
    const emulatorHost = 'localhost:8080';
    const projectId = 'soda-tracker-455105'; // Same as used elsewhere in the app
    const exportUrl = `http://${emulatorHost}/emulator/v1/projects/${projectId}/databases/(default)/documents:export`;

    const token = await getAuthToken();
    const response = await fetch(exportUrl, {
      method: 'GET',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to export Firestore data: ${errorText}`);
    }

    const data = await response.json();
    return {
      data,
      timestamp: new Date().toISOString(),
      success: true
    };
  } catch (error) {
    console.error('Error exporting Firestore data:', error);
    throw error;
  }
}

/**
 * Delete a location with all its dependencies (sightings, etc.)
 * This performs a cascading delete operation
 * @param locationId - ID of the location to delete
 * @returns Promise with the result of the deletion
 */
export async function deleteLocation(locationId: string): Promise<any> {
  try {
    await checkUserPermission();
    
    // First, call assessDeletionImpact to get the impact assessment
    const assessment = await safeHttpsCallable('assessDeletionImpact', {
      documentType: 'location',
      documentId: locationId
    });
    
    // Get the confirmed impact for validation
    // Type the assessment data to match the DeletionImpact structure
    const impactData = assessment.data as {
      totalImpactedDocuments: number;
      primaryDocument: { type: string; id: string; name: string };
      dependentDocuments: Record<string, { count: number; sampleItems?: Array<{id: string, name: string}> }>;
    };
    
    const confirmedImpact = impactData.totalImpactedDocuments;
    
    // Call the deletion function with all required parameters
    const result = await safeHttpsCallable('executeCascadingDeletion', {
      documentType: 'location',
      documentId: locationId,
      confirmedImpact
    });
    
    return result.data;
  } catch (error) {
    console.error('Error deleting location:', error);
    throw error;
  }
}

/**
 * Assess the impact of deleting a location without performing the deletion
 * @param locationId - ID of the location to assess
 * @returns Promise with the assessment result
 */
export async function assessLocationDeletion(locationId: string): Promise<any> {
  try {
    await checkUserPermission();
    
    // Call assessDeletionImpact to get the impact assessment
    const assessment = await safeHttpsCallable('assessDeletionImpact', {
      documentType: 'location',
      documentId: locationId
    });
    
    return assessment.data;
  } catch (error) {
    console.error('Error assessing location deletion:', error);
    throw error;
  }
}

/**
 * Delete a confirmation with all its dependencies
 * @param confirmationId - ID of the confirmation to delete
 * @param sightingId - ID of the parent sighting
 * @returns Promise with the result of the deletion
 */
export async function deleteConfirmation(confirmationId: string, sightingId: string): Promise<any> {
  try {
    await checkUserPermission(); // Ensures user is authenticated and has basic token valid

    const result = await safeHttpsCallable('executeCascadingDeletion', {
      documentType: 'confirmation',
      documentId: confirmationId,
      sightingId: sightingId,
      confirmedImpact: 1 // Confirmations only delete themselves
    });
    
    return result.data;
  } catch (error) {
    console.error('Error deleting confirmation:', error);
    throw error;
  }
}

/**
 * Test function for deletion assessment
 * @param entityType - Type of entity to assess
 * @param entityId - ID of the entity to assess
 * @returns Promise with the assessment result
 */
export async function testDeletionAssessment(entityType: string, entityId: string): Promise<any> {
  try {
    await checkUserPermission();
    
    // Call assessDeletionImpact directly
    const result = await safeHttpsCallable('assessDeletionImpact', {
      documentType: entityType,
      documentId: entityId
    });
    
    return result.data;
  } catch (error) {
    console.error('Error testing deletion assessment:', error);
    throw error;
  }
}